import { MenuProps, type TableProps } from "antd";
import type { NavigateFunction } from "react-router-dom";

export type ApiSortOrder = "asc" | "desc";

/** Configuration for `createSortUrlOnChange`. */
export interface SortUrlOnChangeOptions<TQueryParams> {
  navigate: NavigateFunction;
  apiQueryParams: TQueryParams;
  updateURLWithFilters: (
    navigate: NavigateFunction,
    filters: TQueryParams,
    resetPage?: boolean,
  ) => void;
  defaultSortBy: string;
  defaultSortOrder: ApiSortOrder;
}

/**
 * Creates a table sort handler that syncs sort state to URL params.
 * Resets to default sorting when cleared.
 */
export function createSortUrlOnChange<TRecord, TQueryParams extends object>(
  options: SortUrlOnChangeOptions<TQueryParams>,
): NonNullable<TableProps<TRecord>["onChange"]> {
  const {
    navigate,
    apiQueryParams,
    updateURLWithFilters,
    defaultSortBy,
    defaultSortOrder,
  } = options;

  return (_pagination, _filters, sorter) => {
    if (!Array.isArray(sorter) && sorter.columnKey) {
      const sortBy = String(sorter.columnKey);
      const sortOrder: ApiSortOrder =
        sorter.order === "ascend" ? "asc" : "desc";

      updateURLWithFilters(
        navigate,
        {
          ...apiQueryParams,
          sort_by: sortBy,
          sort_order: sortOrder,
        } as TQueryParams,
        false,
      );
    } else {
      const {
        sort_by: _sortBy,
        sort_order: _sortOrder,
        ...restParams
      } = apiQueryParams as TQueryParams & {
        sort_by?: unknown;
        sort_order?: unknown;
      };
      updateURLWithFilters(
        navigate,
        {
          ...restParams,
          sort_by: defaultSortBy,
          sort_order: defaultSortOrder,
        } as TQueryParams,
        false,
      );
    }
  };
}

export type SupportedValue =
  | string
  | string[]
  | number
  | boolean
  | Record<string, any>
  | null;
export type FilteredSupportedValue = Exclude<SupportedValue, null>;
export type TableData = Record<string, SupportedValue>;
export interface FilterSearchOption {
  enabled?: boolean;
  searchKeys?: string[];
  searchValue?: string;
}

export interface VLTableActionsConfig<T extends TableData = TableData> {
  items: (record: T) => MenuProps["items"];
}

/**
 * Type guard to check if a value can be searched
 */
export const isSupportedSearchableValue = (
  value: unknown,
): value is Exclude<SupportedValue, null> => {
  const type = typeof value;
  return (
    type === "string" ||
    type === "number" ||
    type === "boolean" ||
    Array.isArray(value) ||
    type === null
  );
};

/**
 * Extract unique values from a dataset for a specific column
 * Handles both string and string[] values by flattening arrays
 */
const extractUniqueValues = (
  dataSource: TableData[],
  columnKey: string,
): SupportedValue[] => {
  if (!dataSource.length) return [];

  const allValues = new Set<string>();

  dataSource.forEach((item) => {
    const value = item[columnKey];
    if (isSupportedSearchableValue(value)) {
      if (Array.isArray(value)) {
        // For arrays, add each element separately
        value.forEach((arrayItem) => {
          if (typeof arrayItem === "string" && arrayItem.trim()) {
            allValues.add(arrayItem.trim());
          }
        });
      } else if (typeof value === "string" && value.trim()) {
        allValues.add(value.trim());
      } else if (typeof value === "number" || typeof value === "boolean") {
        allValues.add(String(value));
      }
    }
  });

  return Array.from(allValues);
};

/**
 * Filter out null and undefined values
 */
const filterValidValues = (
  values: SupportedValue[],
): FilteredSupportedValue[] => {
  return values.filter((value): value is FilteredSupportedValue => {
    return value !== null && value !== undefined;
  });
};

/**
 * Sort values with numeric awareness
 */
const sortValues = (values: FilteredSupportedValue[]) => {
  return values.sort((a, b) => {
    const aNum = Number(a);
    const bNum = Number(b);

    // If both are valid numbers, sort numerically
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    // Otherwise sort alphabetically
    return String(a).localeCompare(String(b));
  });
};

/**
 * Map values to filter options compatible with Antd's filter format (String)
 */
const mapToFilterOptions = (
  values: FilteredSupportedValue[],
): { text: string; value: string }[] => {
  return values.map((value) => ({ text: String(value), value: String(value) }));
};

/**
 * Helper function to check if a record value matches a filter value
 * Handles both string and string[] record values
 */
export const recordMatchesFilter = (
  recordValue: SupportedValue,
  filterValue: string,
): boolean => {
  if (recordValue === null || recordValue === undefined) return false;

  const filterLower = filterValue.toLowerCase();

  if (Array.isArray(recordValue)) {
    // For arrays, check if any element contains the filter value
    return recordValue.some((item) =>
      String(item).toLowerCase().includes(filterLower),
    );
  }

  // For non-arrays, convert to string and check
  return String(recordValue).toLowerCase().includes(filterLower);
};

/**
 * Helper function to get sortable string from record value
 * Handles both string and string[] values for consistent sorting
 */
export const getSortableString = (value: SupportedValue): string => {
  if (value === null || value === undefined) return "";

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return String(value);
};

/**
 * Create filter options for a table column
 * Extracts unique values, filters valid ones, sorts them, and maps to filter format
 */
export const createFiltersForColumn = (
  dataSource: TableData[],
  columnKey: string,
): { text: string; value: string }[] => {
  const uniqueValues = extractUniqueValues(dataSource, columnKey);
  const validValues = filterValidValues(uniqueValues);
  const sortedValues = sortValues(validValues);
  return mapToFilterOptions(sortedValues);
};
